import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { restaurantCategoriesService } from '../../services/restaurantCategoriesService';
import { RestaurantCategoryResponse } from '../../types/restaurantCategories';
import '../MainLayout.css';
import './Modal.css';

interface ManageRestaurantCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: number;
  onCategoriesUpdated: () => void;
}

const ManageRestaurantCategoriesModal: React.FC<ManageRestaurantCategoriesModalProps> = ({
  isOpen,
  onClose,
  restaurantId,
  onCategoriesUpdated,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [allCategories, setAllCategories] = useState<RestaurantCategoryResponse[]>([]);
  const [attachedCategoryIds, setAttachedCategoryIds] = useState<Set<number>>(new Set());
  const [initialAttachedIds, setInitialAttachedIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAllCategories();
      fetchAttachedCategories();
    }
  }, [isOpen, restaurantId]);

  const fetchAllCategories = async () => {
    setLoading(true);
    setModalError('');
    try {
      const response = await restaurantCategoriesService.getAllRestaurantCategories();
      if (response.success && response.data) {
        setAllCategories(response.data);
      } else {
        setModalError(response.error || 'Failed to fetch all categories');
      }
    } catch (error) {
      setModalError('Network error while fetching all categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachedCategories = async () => {
    setLoading(true);
    setModalError('');
    try {
      const response = await restaurantCategoriesService.getCategoriesForRestaurant(restaurantId);
      if (response.success && response.data) {
        const ids = new Set(response.data.map(cat => cat.id));
        setAttachedCategoryIds(ids);
        setInitialAttachedIds(new Set(ids)); // Save initial state
      } else {
        setModalError(response.error || 'Failed to fetch attached categories');
      }
    } catch (error) {
      setModalError('Network error while fetching attached categories');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCategory = async (categoryId: number) => {
    setSubmitting(true);
    setModalError('');
    try {
      if (attachedCategoryIds.has(categoryId)) {
        // Detach
        const response = await restaurantCategoriesService.detachCategoryFromRestaurant(categoryId, restaurantId);
        if (response.success) {
          setAttachedCategoryIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(categoryId);
            return newSet;
          });
        } else {
          setModalError(response.error || 'Failed to detach category');
        }
      } else {
        // Attach
        const response = await restaurantCategoriesService.attachCategoryToRestaurant(categoryId, restaurantId);
        if (response.success) {
          setAttachedCategoryIds(prev => new Set(prev).add(categoryId));
        } else {
          setModalError(response.error || 'Failed to attach category');
        }
      }
      // Don't call onCategoriesUpdated here to avoid page refresh
    } catch (error) {
      setModalError('Network error during category update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Check if any changes were made
    const hasChanges =
      attachedCategoryIds.size !== initialAttachedIds.size ||
      Array.from(attachedCategoryIds).some(id => !initialAttachedIds.has(id));

    // Only refresh parent if changes were made
    if (hasChanges) {
      onCategoriesUpdated();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('restaurantDetails.manageCategories')}</h2>
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          {modalError && <div className="error-message" style={{ marginBottom: '1rem' }}>{modalError}</div>}
          {loading ? (
            <div className="loading-container">{t('common.loading')}</div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {allCategories.length === 0 ? (
                <p>{t('restaurantDetails.noCategoriesAvailable')}</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {allCategories.map(category => (
                    <li key={category.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <input
                        type="checkbox"
                        id={`category-${category.id}`}
                        checked={attachedCategoryIds.has(category.id)}
                        onChange={() => handleToggleCategory(category.id)}
                        disabled={submitting}
                        style={{ marginRight: '0.75rem', width: '18px', height: '18px' }}
                      />
                      <label htmlFor={`category-${category.id}`} style={{ flex: 1, cursor: 'pointer' }}>
                        {category.title} (ID: {category.id})
                      </label>
                      {category.imageUrl && (
                        <img
                          src={category.imageUrl}
                          alt={category.title}
                          style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="action-button secondary" onClick={handleClose} disabled={submitting}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageRestaurantCategoriesModal;
